import React, { useState } from 'react';
import { BCPLimits } from '../../types/bcp';
import SubscriptionUpgradeModal from '../SubscriptionUpgradeModal';

interface BCPLimitsModalProps {
  isOpen: boolean;
  onClose: () => void;
  limits: BCPLimits | null;
  loading: boolean;
  onUpgrade?: () => void;
}

const BCPLimitsModal: React.FC<BCPLimitsModalProps> = ({
  isOpen,
  onClose,
  limits,
  loading,
  onUpgrade
}) => {
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'suggested' | 'premium' | 'essential'>('suggested');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                {loading ? (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                ) : limits?.can_create ? (
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                )}
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  {loading ? 'Checking BCP Limits...' :
                   limits?.can_create ? 'BCP Creation Available' : 
                   limits?.plan_type === 'none' ? 'No Active Subscription' : 'BCP Creation Limit Reached'}
                </h3>
                <div className="mt-2">
                  {loading ? (
                    <p className="text-sm text-gray-500">Please wait while we check your subscription limits...</p>
                  ) : limits ? (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600">{limits.message}</p>

                      {/* Usage Progress */}
                      {limits.plan_type !== 'none' && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-700">BCPs Created</span>
                            <span className="text-sm text-gray-600">{limits.current_count}/{limits.limit}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${
                                limits.can_create ? 'bg-green-600' : 'bg-red-600'
                              }`}
                              style={{ width: `${Math.min((limits.current_count / limits.limit) * 100, 100)}%` }}
                            ></div>
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            {limits.plan_type === 'admin' ? 'Unlimited BCPs' :
                             `${limits.limit - limits.current_count} BCPs remaining this year`}
                          </div>
                        </div>
                      )}

                      {/* No Subscription - Show Subscription Options */}
                      {limits.plan_type === 'none' && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-blue-800 mb-2">Get Started with a Subscription</h4>
                          <p className="text-sm text-blue-700 mb-3">Choose a plan to start creating Business Continuity Plans for your business.</p>
                          <div className="space-y-2">
                            <button
                              onClick={() => {
                                setSelectedPlan('essential');
                                setShowSubscriptionModal(true);
                              }}
                              className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              Start with Essential Plan
                            </button>
                            <button
                              onClick={() => {
                                setSelectedPlan('premium');
                                setShowSubscriptionModal(true);
                              }}
                              className="w-full inline-flex justify-center items-center px-4 py-2 border border-blue-600 text-sm font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              Get Premium Plan
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Upgrade Suggestion */}
                      {limits.upgrade_suggestion && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-blue-800 mb-2">Upgrade Your Plan</h4>
                          <p className="text-sm text-blue-700 mb-3">{limits.upgrade_suggestion.message}</p>
                          <div className="space-y-2">
                            <button
                              onClick={() => {
                                setSelectedPlan('suggested');
                                setShowSubscriptionModal(true);
                              }}
                              className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              Purchase {limits.upgrade_suggestion.suggested_plan?.toUpperCase()} Plan
                            </button>
                            <button
                              onClick={() => {
                                setSelectedPlan('premium');
                                setShowSubscriptionModal(true);
                              }}
                              className="w-full inline-flex justify-center items-center px-4 py-2 border border-blue-600 text-sm font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              Purchase Premium Plan
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Plan Details */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-800 mb-2">Current Plan: {limits.plan_type.toUpperCase()}</h4>
                        <div className="text-sm text-gray-600">
                          {limits.plan_type === 'none' && 'No active subscription • Subscribe to get started'}
                          {limits.plan_type === 'essential' && '1 BCP per year • Basic features'}
                          {limits.plan_type === 'gold' && '2 BCPs per year • Advanced features'}
                          {limits.plan_type === 'premium' && '3 BCPs per year • All features'}
                          {limits.plan_type === 'admin' && 'Unlimited BCPs • Admin access'}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Unable to load BCP limits. Please try again.</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="button"
              className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              {limits?.can_create ? 'Continue' : 'Close'}
            </button>
          </div>
        </div>
      </div>

      {/* Subscription Upgrade Modal */}
      {showSubscriptionModal && (limits?.upgrade_suggestion?.suggested_plan || limits?.plan_type === 'none') && (
        <SubscriptionUpgradeModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          requiredFeature={selectedPlan === 'premium' ? 'premium' : 
                          limits.plan_type === 'none' ? selectedPlan as 'essential' | 'gold' | 'premium' :
                          (limits.upgrade_suggestion?.suggested_plan as 'essential' | 'gold' | 'premium') || 'essential'}
          featureName="BCP Creation"
          selectedService="bcp_creation"
          serviceContent={{
            title: "Unlock More BCPs",
            summary: "Create additional Business Continuity Plans with your upgraded subscription",
            description: "Upgrade your plan to create more Business Continuity Plans and access advanced features for comprehensive business resilience planning.",
            benefits: [
              "Create more BCPs per year",
              "Access to advanced AI analysis",
              "Priority support",
              "Additional compliance features"
            ]
          }}
        />
      )}
    </div>
  );
};

export default BCPLimitsModal;
